import React, { useEffect, useState, useLayoutEffect, useMemo } from "react";
import styles from "./BaseModal.module.css";
import { useGoogleFonts } from "@/hooks/useGoogleFonts";
import { createPortal } from "react-dom";
import { DesignSystem } from "@/types/ThemeTypes";
import { isDarkColor } from "@/utils/isDarkColor";

export interface ResponsiveWidths {
  MOBILE: number; // container 375px
  TABLET: number; // container 800px
  DESKTOP: number; // container 1280px
  LARGE_DESKTOP: number; // container 1920px
}

// Default responsive widths for modals
export const DEFAULT_RESPONSIVE_WIDTHS: ResponsiveWidths = {
  MOBILE: 327, // container 375px
  TABLET: 720, // container 800px
  DESKTOP: 1080, // container 1280px
  LARGE_DESKTOP: 1080, // container 1920px
} as const;

interface BaseModalProps {
  open: boolean;
  handleClose: () => void;
  children: React.ReactNode;
  width?: string;
  maxHeight?: string;
  borderRadius?: number;
  container?: string;
  title?: string;
  fontFamily?: string;
  designSystem?: DesignSystem;
  responsiveWidths?: ResponsiveWidths;
  mobileMaxWidth?: string;
}

// Helper function to get container element with fallback chain
const getContainerElement = (containerId?: string): HTMLElement => {
  return (
    (containerId ? document.getElementById(containerId) : null) ||
    document.getElementById("theme-root-container") ||
    document.body
  );
};

const BaseModal: React.FC<BaseModalProps> = ({
  open,
  handleClose,
  children,
  width = "100%",
  maxHeight = "90vh",
  borderRadius = 12,
  container,
  title,
  fontFamily = "Poppins",
  designSystem,
  responsiveWidths = DEFAULT_RESPONSIVE_WIDTHS,
  mobileMaxWidth = "95%",
}) => {
  // Load the font used in this component
  useGoogleFonts([fontFamily]);

  const [computedModalWidth, setComputedModalWidth] = useState<number | null>(
    null
  );
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [modalContainer, setModalContainer] = useState<HTMLElement | null>(
    null
  );

  // Calculate initial width synchronously to prevent flicker
  const initialWidth = useMemo(() => {
    if (!open) return null;

    const containerEl = getContainerElement(container);

    if (containerEl) {
      const containerWidthValue = containerEl.getBoundingClientRect().width;

      // Function to get modal width based on container width and mapping
      if (containerWidthValue >= responsiveWidths.LARGE_DESKTOP) {
        return responsiveWidths.LARGE_DESKTOP;
      }
      if (containerWidthValue >= responsiveWidths.DESKTOP) {
        return responsiveWidths.DESKTOP;
      }
      if (containerWidthValue >= responsiveWidths.TABLET) {
        return responsiveWidths.TABLET;
      }
      // For any width smaller than TABLET modal width, use MOBILE
      return responsiveWidths.MOBILE;
    }
    return null;
  }, [open, responsiveWidths, container]);

  // Set initial width immediately to prevent flicker
  useLayoutEffect(() => {
    if (!open) {
      setComputedModalWidth(null);
      setContainerWidth(null);
      return;
    }

    const containerEl = getContainerElement(container);

    // Use the pre-calculated initial width immediately
    if (initialWidth !== null) {
      setComputedModalWidth(initialWidth);
    }

    // Set initial container width
    if (containerEl) {
      const initialContainerWidth = containerEl.getBoundingClientRect().width;
      setContainerWidth(initialContainerWidth);
    }

    const detectAndSetWidth = () => {
      const containerEl = getContainerElement(container);

      if (containerEl) {
        const containerWidthValue = containerEl.getBoundingClientRect().width;

        // Calculate modal width
        let modalWidth: number;
        if (containerWidthValue >= responsiveWidths.LARGE_DESKTOP) {
          modalWidth = responsiveWidths.LARGE_DESKTOP;
        } else if (containerWidthValue >= responsiveWidths.DESKTOP) {
          modalWidth = responsiveWidths.DESKTOP;
        } else if (containerWidthValue >= responsiveWidths.TABLET) {
          modalWidth = responsiveWidths.TABLET;
        } else {
          modalWidth = responsiveWidths.MOBILE;
        }

        setContainerWidth(containerWidthValue);
        setComputedModalWidth(modalWidth);
      }
    };

    if (containerEl) {
      const resizeObserver = new ResizeObserver(detectAndSetWidth);
      resizeObserver.observe(containerEl);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [open, responsiveWidths, container, initialWidth]);

  // Get responsive styles based on computed modal width
  const getResponsiveStyles = () => {
    // Use computed width from responsive mapping
    if (computedModalWidth !== null) {
      if (
        containerWidth !== null &&
        containerWidth <= responsiveWidths.TABLET
      ) {
        return {
          width: mobileMaxWidth,
          maxWidth: mobileMaxWidth,
          maxHeight: maxHeight,
        };
      }

      return {
        width: `${computedModalWidth}px`,
        maxWidth: `${computedModalWidth}px`,
        maxHeight: maxHeight,
      };
    }
    // Fallback to default width prop if no responsive mapping
    return {
      width: width,
      maxHeight: maxHeight,
    };
  };

  const responsiveStyles = getResponsiveStyles();

  useEffect(() => {
    const el = container
      ? (document.getElementById(container) as HTMLElement)
      : document.querySelector("body");
    setModalContainer(el);
  }, [container]);

  useEffect(() => {
    if (open) {
      // Always disable scroll on body
      const body = document.body;
      const originalBodyOverflow = body.style.overflow;
      body.style.overflow = "hidden";

      // Also disable scroll on container if it's different from body
      let originalContainerOverflow: string | null = null;
      if (modalContainer && modalContainer !== body) {
        originalContainerOverflow = modalContainer.style.overflow;
        modalContainer.style.overflow = "hidden";
      }

      // Cleanup function - restore original values when modal closes
      return () => {
        body.style.overflow = originalBodyOverflow;
        if (
          modalContainer &&
          modalContainer !== body &&
          originalContainerOverflow !== null
        ) {
          modalContainer.style.overflow = originalContainerOverflow;
        }
      };
    }
  }, [open, modalContainer]);

  if (!open) return null;

  const headerBackground = designSystem?.background || "#ffffff";
  const isDark = isDarkColor(headerBackground);
  const headerColor = isDark ? "white" : "black";

  const CloseIcon = () => (
    <svg
      className={styles.bb_minimalism_base_modal_close_icon}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  );

  return createPortal(
    <div className={styles.bb_minimalism_base_modal_overlay}>
      <div
        className={styles.bb_minimalism_base_modal_container}
        style={{
          ...responsiveStyles,
          borderRadius: `${borderRadius}px`,
          fontFamily,
        }}
      >
        <div
          className={styles.bb_minimalism_base_modal_header}
          style={{ backgroundColor: designSystem?.background || "#ffffff" }}
        >
          <div className={styles.bb_minimalism_base_modal_title_section}>
            <h2
              className={styles.bb_minimalism_base_modal_title}
              style={{ color: headerColor }}
            >
              {title}
            </h2>
          </div>
          <div
            className={styles.bb_minimalism_base_modal_close_button}
            onClick={handleClose}
            role="button"
            tabIndex={0}
            aria-label="close"
            style={{
              cursor: "pointer",
              color: headerColor,
            }}
          >
            <CloseIcon />
          </div>
        </div>
        <div className={styles.bb_minimalism_base_modal_content}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BaseModal;
